<template>
  <div class="card p-3 h-100 border-gradient confirm-leave-clan">
    <div class="d-flex justify-content-center">
      <TitleContent :text="$$t('Leave Clan', 'title')" />
    </div>
    <div class="text-center">
      {{ textChangeRole }}
    </div>
    <BreadcrumbAlt
      v-if="isOwner && isCaptain"
      class="mt-3"
      :items="$options.breadcrumItems"
      :current-step="currentStep"
    />
    <HeraDummyList
      v-if="isLoading"
      icon-name="dummy/members"
      size="200"
      position="center"
    >
      {{ $$t('Fetching list of users', 'fetching') }}
    </HeraDummyList>
    <template v-else>
      <div
        v-if="isShowCaptainForm"
        class="card-body col-10 h-100 align-self-center mt-3 h-100 flex-grow-1 custom-scrollbar"
      >
        <div v-for="(item, index) in listTeams" :key="index" class="mt-4">
          <template v-if="item.members.length > 0">
            <HeraFormDropdown
              :list="item.members"
              variant="input-like"
              :label="
                $$t('Choose new captain {team}', 'choose captain', {
                  team: item.team.name,
                })
              "
              @input="({ text: login }) => onSelectCaptain(item.team, login)"
            />
          </template>
          <div v-else>
            {{
              $$t(
                'You are the only team member of {team} team? If you leave this team it will be deleted automatically',
                'delete-team-msg',
                { team: item.team.name }
              )
            }}
          </div>
        </div>
      </div>
      <div v-else class="col-10 align-self-center mt-3">
        <HeraFormDropdown
          v-model="chosenOwner"
          :list="clanMembersList"
          variant="input-like"
          :label="$$t('Choose new owner')"
        />
      </div>
    </template>
    <div class="mt-4 d-flex justify-content-center ">
      <HeraFormBtn class="h-mr-2" @click.prevent="closeModal">{{
        $$t('Cancel')
      }}</HeraFormBtn>
      <HeraFormBtn class="btn-primary" @click.prevent="onSubmit">{{
        textButtonConfirm
      }}</HeraFormBtn>
    </div>
  </div>
</template>

<script>
import { mapActions, mapMutations, mapState } from 'vuex';
import { translate } from '@/mixins/translate';
import isLoading from '@/mixins/isLoading';
import { prop } from '@/utils/factories';
import HeraFormDropdown from '@/components/molecules/form/HeraFormDropdown';
import { CLAN_ROLES, PROFILE } from '@/constants/accounts';
import HeraFormBtn from '@/components/atoms/HeraFormBtn';
import TitleContent from '@/components/text/TitleContent';
import HeraDummyList from '@/components/common/HeraDummyList';
import BreadcrumbAlt from '@/components/common/BreadcrumbAlt';

export default {
  name: 'ClanLeaveConfirm',
  components: {
    HeraFormDropdown,
    HeraFormBtn,
    TitleContent,
    HeraDummyList,
    BreadcrumbAlt,
  },
  mixins: [isLoading, translate('_web_clan-leave-confirm')],
  props: {
    isCaptain: prop(Boolean),
    isOwner: prop(Boolean),
    myTeams: prop(Array),
  },
  data() {
    return {
      chosenOwner: null,
      clanMembersList: [],
      listTeams: [],
      isShowCaptainForm: false,
      selectedCaptains: [],
    };
  },
  computed: {
    ...mapState('current', {
      clanAccount: 'account',
      clanAccountId: 'accountId',
    }),
    ...mapState('my', ['account', 'accountId']),
    listForDeleteTeams() {
      return this.listTeams.filter(({ members }) => !members.length);
    },
    currentStep() {
      return this.isShowCaptainForm ? 1 : 2;
    },
    textChangeRole() {
      return this.isOnlyCaptain
        ? this.$$t(
            'You cant leave clan in captain role. You have to delete your captain role to another team member',
            'change-captain-text-team'
          )
        : this.isOnlyOwner
        ? this.$$t(
            'You cant leave team in owner role. You have to delete your owner role to another team member',
            'change-owner-text-team'
          )
        : this.$$t(
            'You cant leave team in team captain and team owner roles. You have to choose another captain from team members and another team owner from team members',
            'change-owner-captain-text-team'
          );
    },
    isOnlyCaptain() {
      return !this.isOwner && this.isCaptain;
    },
    isOnlyOwner() {
      return this.isOwner && !this.isCaptain;
    },
    textButtonConfirm() {
      return this.isOwner && this.isCaptain
        ? this.$$t('Next')
        : this.$$t('Leave');
    },
  },
  created() {
    this.$options.breadcrumItems = [
      {
        icon: 'people',
        label: this.$$t('Delegate captains role'),
      },
      {
        icon: 'shield',
        label: this.$$t('Delegate owners role'),
      },
    ];

    this.setLoading();
    if (this.isCaptain) {
      this.getPageData()
        .then(() => {
          this.isShowCaptainForm = true;
        })
        .finally(this.unsetLoading);
    } else {
      this.isShowCaptainForm = false;
    }
    if (this.isOwner) {
      this.getClanMembers().finally(this.unsetLoading);
    }
  },
  methods: {
    ...mapMutations('current', ['updateAccountModel']),
    ...mapMutations('my/clans', ['updateClan']),
    ...mapMutations(['updateAccountKey']),
    ...mapActions([
      'fetchClanMembers',
      'updateRoleInClan',
      'closeModal',
      'errorNotify',
      'cancelUserFromMyClan',
      'fetchClanTeamMembers',
      'updateCaptainInTeam',
      'deleteTeamInClan',
    ]),
    ...mapActions('my', ['fetchMyAccount']),
    getClanMembers() {
      return this.fetchClanMembers({ accountId: this.clanAccountId })
        .then(({ items }) => {
          this.clanMembersList = items
            .filter(({ role }) => role !== CLAN_ROLES.OWNER)
            .map(({ login }) => ({ text: login }));
        })
        .then(() => (this.chosenOwner = this.clanMembersList[0]))
        .catch(this.errorNotify);
    },
    getPageData() {
      return Promise.all(
        this.myTeams.map(team =>
          this.fetchClanTeamMembers({
            clanID: this.clanAccountId,
            teamID: team.id,
          })
        )
      )
        .then(teams => {
          return teams.map(arr => {
            return arr
              .filter(member => member.login !== this.account.login)
              .map(member => ({ text: member.login }));
          });
        })
        .then(membersList => {
          this.listTeams = this.myTeams.reduce((acc, team, index) => {
            if (membersList[index].length > 0) {
              this.onSelectCaptain(team, membersList[index][0].text);
            }
            return [
              ...acc,
              {
                team,
                members: membersList[index],
              },
            ];
          }, []);
        })
        .catch(this.errorNotify);
    },
    onSubmit() {
      if (this.isOnlyCaptain) {
        this.changeTeamsInClan()
          .then(this.updateAccountKey)
          .then(this.leaveClan)
          .catch(this.errorNotify);
      } else if (this.isOnlyOwner) {
        this.changeOwner()
          .then(this.leaveClan)
          .catch(this.errorNotify);
      } else {
        if (this.isShowCaptainForm) {
          this.changeTeamsInClan()
            .then(() => {
              this.updateAccountKey();
              this.isShowCaptainForm = false;
            })
            .catch(this.errorNotify);
        } else {
          this.changeOwner()
            .then(this.leaveClan)
            .catch(this.errorNotify);
        }
      }
    },
    changeTeamsInClan() {
      if (this.listForDeleteTeams.length) {
        return this.deleteTeams()
          .then(() => {
            if (this.selectedCaptains.length) {
              this.changeCaptains();
            }
          })
          .catch(this.errorNotify);
      } else {
        return this.changeCaptains();
      }
    },
    deleteTeams() {
      return Promise.all(
        this.listForDeleteTeams.map(elem => {
          const data = {
            clanID: this.clanAccountId,
            teamID: elem.team.id,
          };
          return this.deleteTeamInClan(data).catch(this.errorNotify);
        })
      );
    },
    onSelectCaptain(team, captain) {
      let index = this.selectedCaptains.findIndex(
        elem => elem.teamID === team.id
      );
      if (index >= 0) {
        this.$set(this.selectedCaptains[index].captain, 'login', captain);
      } else {
        this.selectedCaptains.push({
          clanID: this.clanAccountId,
          teamID: team.id,
          captain: {
            login: captain,
          },
        });
      }
    },
    changeCaptains() {
      return Promise.all(
        this.selectedCaptains.map(elem => {
          return this.updateCaptainInTeam(elem).catch(this.errorNotify);
        })
      );
    },
    changeOwner() {
      if (!this.chosenOwner) {
        this.errorNotify(
          this.$$t('Please choose new owner', 'choose-new-owner')
        );
      } else {
        return this.updateRoleInClan({
          clanID: this.clanAccountId,
          login: this.chosenOwner.text,
          role: {
            role: CLAN_ROLES.OWNER,
          },
        })
          .then(() => {
            // use 1 model in feature
            //change to multiclan here
            this.updateAccountModel({ role: CLAN_ROLES.MEMBER });

            this.updateClan({
              id: this.clanAccountId,
              role: CLAN_ROLES.MEMBER,
            });
          })
          .catch(this.errorNotify);
      }
    },
    leaveClan() {
      this.cancelUserFromMyClan({
        clanID: this.clanAccountId,
        login: this.accountId,
      })
        .then(() => {
          this.closeModal();
          this.goToMyAccount();
        })
        .then(this.fetchMyAccount)
        .catch(this.errorNotify);
    },
    goToMyAccount() {
      this.$router.push({
        name: this.$routerNames.ACCOUNT,
        params: { accountType: PROFILE, accountId: this.accountId },
      });
    },
  },
};
</script>
<style lang="scss">
.confirm-leave-clan {
  background: $ph-card;
  background: transparent !important;
  border: none !important;
  border-radius: $ph-small-radius;
  max-height: calc(100vh - 2rem);

  .header-item-btn {
    width: auto;
    min-width: auto;
  }
  .separator {
    height: 2px;
    background-color: $ph-secondary-text;
  }
}
</style>
